import { Injectable } from '@angular/core';
import { HttpClient } from '@angular/common/http';
import { BehaviorSubject, Observable, of } from 'rxjs';
import { catchError, finalize, shareReplay, tap } from 'rxjs/operators';
import { BE_API_AUTH_URL } from '../constants/app.constants';
import { AuthService } from './auth.service';

export interface PlatformAnnouncementTarget {
  commands: readonly string[];
  queryParams?: Readonly<Record<string, string>>;
}

export const PLATFORM_ANNOUNCEMENT_TARGETS = {
  dashboard: { commands: ['/dashboard'] },
  properties: { commands: ['/properties'] },
  company: { commands: ['/company'] },
  qr_template: { commands: ['/qr-poster-template'] },
  qr_sign_order: { commands: ['/qr-poster-template'], queryParams: { signOrderMode: 'new' } },
  announcements: { commands: ['/announcements'] },
  audit_logs: { commands: ['/audit-logs'] },
  account_settings: { commands: ['/account/settings/account'] },
  billing: { commands: ['/account/settings/billing'] },
  team_access: { commands: ['/account/settings/team-access'] },
  integrations: { commands: ['/company/integrations'] },
} as const satisfies Record<string, PlatformAnnouncementTarget>;

export type PlatformAnnouncementTargetKind = keyof typeof PLATFORM_ANNOUNCEMENT_TARGETS;

export interface PlatformAnnouncement {
  _id: string;
  messageDe: string;
  messageEn: string | null;
  active: boolean;
  targetKind: PlatformAnnouncementTargetKind | null;
  createdAt: string;
  updatedAt: string;
}

export interface ActivePlatformAnnouncement {
  _id: string;
  messageDe: string;
  messageEn: string | null;
  targetKind: PlatformAnnouncementTargetKind | null;
  updatedAt: string;
}

export function isPlatformAnnouncementTargetKind(value: unknown): value is PlatformAnnouncementTargetKind {
  return typeof value === 'string' && Object.prototype.hasOwnProperty.call(PLATFORM_ANNOUNCEMENT_TARGETS, value);
}

@Injectable({ providedIn: 'root' })
export class PlatformAnnouncementService {
  private readonly activeSubject = new BehaviorSubject<ActivePlatformAnnouncement | null>(null);
  private loaded = false;
  private loadingRequest?: Observable<ActivePlatformAnnouncement | null>;

  readonly active$ = this.activeSubject.asObservable();

  constructor(private http: HttpClient, private auth: AuthService) {}

  loadActiveOnce(): Observable<ActivePlatformAnnouncement | null> {
    if (!this.auth.isAuthenticated() || this.auth.isCurrentUserAdmin()) {
      this.reset();
      return of(null);
    }
    if (this.loaded) return of(this.activeSubject.value);
    if (this.loadingRequest) return this.loadingRequest;

    this.loadingRequest = this.http
      .get<ActivePlatformAnnouncement | null>(`${BE_API_AUTH_URL}/platform-announcements/active`, {
        headers: this.auth.createAuthorizationHeader(),
      })
      .pipe(
        tap((announcement) => {
          this.activeSubject.next(this.normalizeActiveAnnouncement(announcement));
          this.loaded = true;
        }),
        catchError(() => {
          // A transient failure is intentionally quiet, but it must not poison
          // the per-session cache as a successful "no announcement" response.
          this.loaded = false;
          return of(null);
        }),
        finalize(() => {
          this.loadingRequest = undefined;
        }),
        shareReplay(1),
      );

    return this.loadingRequest;
  }

  reset(): void {
    this.loaded = false;
    this.loadingRequest = undefined;
    this.activeSubject.next(null);
  }

  list(): Observable<PlatformAnnouncement[]> {
    return this.http.get<PlatformAnnouncement[]>(`${BE_API_AUTH_URL}/admin/platform-announcements`, {
      headers: this.auth.createAuthorizationHeader(),
    });
  }

  get(id: string): Observable<PlatformAnnouncement> {
    return this.http.get<PlatformAnnouncement>(`${BE_API_AUTH_URL}/admin/platform-announcements/${encodeURIComponent(id)}`, {
      headers: this.auth.createAuthorizationHeader(),
    });
  }

  create(payload: Pick<PlatformAnnouncement, 'messageDe' | 'messageEn' | 'targetKind'>): Observable<PlatformAnnouncement> {
    return this.http.post<PlatformAnnouncement>(`${BE_API_AUTH_URL}/admin/platform-announcements`, payload, {
      headers: this.auth.createAuthorizationHeader(),
    });
  }

  update(id: string, payload: Pick<PlatformAnnouncement, 'messageDe' | 'messageEn' | 'targetKind'>): Observable<PlatformAnnouncement> {
    return this.http.put<PlatformAnnouncement>(`${BE_API_AUTH_URL}/admin/platform-announcements/${encodeURIComponent(id)}`, payload, {
      headers: this.auth.createAuthorizationHeader(),
    });
  }

  activate(id: string): Observable<PlatformAnnouncement> {
    return this.http.post<PlatformAnnouncement>(`${BE_API_AUTH_URL}/admin/platform-announcements/${encodeURIComponent(id)}/activate`, {}, {
      headers: this.auth.createAuthorizationHeader(),
    });
  }

  deactivate(id: string): Observable<PlatformAnnouncement> {
    return this.http.post<PlatformAnnouncement>(`${BE_API_AUTH_URL}/admin/platform-announcements/${encodeURIComponent(id)}/deactivate`, {}, {
      headers: this.auth.createAuthorizationHeader(),
    });
  }

  delete(id: string): Observable<void> {
    return this.http.delete<void>(`${BE_API_AUTH_URL}/admin/platform-announcements/${encodeURIComponent(id)}`, {
      headers: this.auth.createAuthorizationHeader(),
    });
  }

  private normalizeActiveAnnouncement(value: ActivePlatformAnnouncement | null): ActivePlatformAnnouncement | null {
    if (!value || typeof value._id !== 'string' || !value.messageDe?.trim()) return null;
    return {
      _id: value._id,
      messageDe: value.messageDe.trim(),
      messageEn: typeof value.messageEn === 'string' && value.messageEn.trim() ? value.messageEn.trim() : null,
      targetKind: isPlatformAnnouncementTargetKind(value.targetKind) ? value.targetKind : null,
      updatedAt: value.updatedAt,
    };
  }
}
